import {useReports} from '@sail/observability';
import {useUpdatedRef} from '@sail/react';
import {useCallback, useContext, useMemo} from 'react';
import HrefContext, {defaultSetHref} from 'src/internal/contexts/HrefContext';

import type {Href, NavigationOptions} from 'src/types';

/**
 * @see https://trailhead.corp.stripe.com/docs/sail-navigation/api/useHref
 */
export default function useHref(): [
  Href,
  (
    newHref: Href | ((oldHref: Href) => Href),
    options?: NavigationOptions,
  ) => void,
] {
  const [href, setHref] = useContext(HrefContext);
  const {error} = useReports();
  const hrefRef = useUpdatedRef(href);
  const setHrefRef = useUpdatedRef(setHref);

  if (setHref === defaultSetHref) {
    error(
      'Setters for `href` are not configuring, navigating using `@sail/navigation` will not work.',
    );
  }

  const setDeduplicatedHref = useCallback(
    (
      newHref: Href | ((oldHref: Href) => Href),
      options?: NavigationOptions,
    ) => {
      const normalizeOptions = options ?? {behavior: 'default'};
      const resolvedHref =
        typeof newHref === 'function' ? newHref(hrefRef.current) : newHref;

      if (
        hrefRef.current !== resolvedHref ||
        couldNavigateToSameHref(options)
      ) {
        setHrefRef.current(resolvedHref, normalizeOptions);
      }
    },
    [setHrefRef, hrefRef],
  );

  return useMemo(
    () => [href, setDeduplicatedHref],
    [href, setDeduplicatedHref],
  );
}

function couldNavigateToSameHref(options?: NavigationOptions): boolean {
  return (
    !!options && (options.behavior === 'blank' || options.behavior === 'reload')
  );
}
